import { IS_DEV } from '@/lib/env';

/**
 * Tracing for the offline machinery.
 *
 * On in development, and switchable on in production from the console:
 *
 *     localStorage.setItem('tlp.debug', '1')   // then reload
 *     localStorage.removeItem('tlp.debug')
 *
 * That switch is the point of this file. The failures worth debugging here do
 * not happen on a developer's machine — they happen on a counter, on a shop's
 * wifi, halfway through a queue of customers, and the only way to find out what
 * the till believed at the time is to be able to turn tracing on where it is
 * standing.
 *
 * **Never log bill contents.** A queued sale carries a customer's name, phone
 * number and what medicine they bought. That belongs in the outbox, not in a
 * console that ends up in a screenshot pasted into a support chat. Everything
 * below logs shapes, counts and ids — never a payload.
 */

const KEY = 'tlp.debug';

function enabled(): boolean {
  if (IS_DEV) return true;
  try {
    return localStorage.getItem(KEY) === '1';
  } catch {
    // A locked-down profile that refuses localStorage should not crash on a log.
    return false;
  }
}

type Area = 'net' | 'outbox' | 'sync' | 'cache' | 'till';

const COLOUR: Record<Area, string> = {
  net: '#0ea5e9',
  outbox: '#8b5cf6',
  sync: '#16a34a',
  cache: '#d97706',
  till: '#0f766e',
};

function at(): string {
  // Wall-clock rather than elapsed: these lines get read next to a pharmacist
  // saying "it stopped working around half past four".
  return new Date().toLocaleTimeString('en-GB', { hour12: false });
}

function emit(area: Area, level: 'log' | 'warn' | 'error', message: string, detail?: unknown) {
  if (!enabled()) return;
  const prefix = `%c${at()} ${area}%c ${message}`;
  const style = `color:${COLOUR[area]};font-weight:600`;
  if (detail === undefined) console[level](prefix, style, '');
  else console[level](prefix, style, '', detail);
}

export const log = {
  net: (message: string, detail?: unknown) => emit('net', 'log', message, detail),
  outbox: (message: string, detail?: unknown) => emit('outbox', 'log', message, detail),
  sync: (message: string, detail?: unknown) => emit('sync', 'log', message, detail),
  cache: (message: string, detail?: unknown) => emit('cache', 'log', message, detail),
  till: (message: string, detail?: unknown) => emit('till', 'log', message, detail),

  warn: (area: Area, message: string, detail?: unknown) => emit(area, 'warn', message, detail),
  /** Errors are logged whether or not tracing is on: they are never noise. */
  error: (area: Area, message: string, detail?: unknown) =>
    console.error(`${at()} ${area} ${message}`, detail ?? ''),

  /** Whether tracing is on, for the one-line banner at startup. */
  get enabled() {
    return enabled();
  },
};

/**
 * A snapshot of what the till currently believes, for `window.tlpOffline()`.
 *
 * Wired up in the provider. It exists so a support call can be "open the
 * console and type tlpOffline()" rather than a walk through four screens.
 */
export function exposeDebugHook(read: () => unknown): void {
  (globalThis as Record<string, unknown>).tlpOffline = () => {
    const state = read();
    console.table?.(state as never);
    return state;
  };
}
